/**
 * UranIA PA — persistenza sessione onboarding PA reload-safe (US-015 AC#3).
 *
 * AC US-015 letterale: "Progress saved on every step transition via POST
 * /api/v1/onboarding/pa/sessions/{id}/steps/{n}" + AC#3 "page reload-safe".
 *
 * Implementazione:
 *   - Source of truth: backend (la session ha `current_step` lato Postgres,
 *     migration 0002, US-012). Allo (re)open la SPA chiama
 *     `createOnboardingPaSession` (idempotente per UNIQUE
 *     (tenant_id, user_id, profile_type) — US-013 AC#1) e riceve
 *     `current_step` ufficiale dal server.
 *   - Cache di transito: `sessionStorage` (NON `localStorage`) custodisce
 *     `session_id` + `current_step` indicizzato per `subject` (Keycloak
 *     `sub`). Cosi' un reload di tab esistente riprende la wizard al punto
 *     giusto SENZA dover ri-postare al server in race. Quando il browser
 *     tab chiude la sessione, lo storage svanisce (per design — niente
 *     PII residente).
 *   - Niente PII nel storage: solo ULID session_id + numero step + subject.
 *     Niente nome/CF: il server e' la source-of-truth dei payload step.
 *
 * Boundary di sicurezza (analisi §18):
 *   - sessionStorage e' XSS-vulnerable come ogni storage client-side. Per
 *     ridurre l'impatto, qui custodiamo solo identificativi opachi
 *     (session ULID, step int): un attacker che ne entra in possesso non
 *     puo' avanzare la wizard senza un bearer JWT valido (la rotta
 *     `/steps/{n}` richiede step-up MFA RFC 9470 — US-013).
 *
 * Tipi:
 *   - `OnboardingPaSnapshot` salva session_id + current_step + subject. Un
 *     mismatch di subject (utente diverso accede dallo stesso browser)
 *     invalida lo snapshot via `loadOnboardingPaSnapshot`.
 */

import type { OnboardingPaSessionDto } from './pa-schemas.ts';

const STORAGE_KEY = 'urania.onboarding.pa.session.v1';

export interface OnboardingPaSnapshot {
  readonly subject: string;
  readonly sessionId: string;
  readonly currentStep: number;
  readonly completed: boolean;
}

function safeStorage(): Storage | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function saveOnboardingPaSnapshot(snapshot: OnboardingPaSnapshot): void {
  const storage = safeStorage();
  if (storage === null) {
    return;
  }
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // QuotaExceededError, SecurityError in iframe sandboxati: best-effort.
  }
}

export function loadOnboardingPaSnapshot(subject: string): OnboardingPaSnapshot | null {
  if (subject.length === 0) {
    return null;
  }
  const storage = safeStorage();
  if (storage === null) {
    return null;
  }
  let raw: string | null;
  try {
    raw = storage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (raw === null) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<OnboardingPaSnapshot>;
    if (
      typeof parsed.subject !== 'string' ||
      typeof parsed.sessionId !== 'string' ||
      typeof parsed.currentStep !== 'number' ||
      typeof parsed.completed !== 'boolean'
    ) {
      return null;
    }
    if (parsed.subject !== subject) {
      return null;
    }
    return {
      subject: parsed.subject,
      sessionId: parsed.sessionId,
      currentStep: parsed.currentStep,
      completed: parsed.completed,
    };
  } catch {
    return null;
  }
}

export function clearOnboardingPaSnapshot(): void {
  const storage = safeStorage();
  if (storage === null) {
    return;
  }
  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    // best-effort.
  }
}

export function snapshotFromSessionDto(
  subject: string,
  session: OnboardingPaSessionDto,
): OnboardingPaSnapshot {
  return {
    subject,
    sessionId: session.session_id,
    currentStep: session.current_step,
    completed: typeof session.completed_at === 'string' && session.completed_at.length > 0,
  };
}
